import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  TextField,
  Typography,
  Link,
  Dialog,
  DialogContent,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useProjectStore } from '../../store/projectStore';
import { useLineStore } from '../../store/lineStore';
import { useIssueStore } from '../../store/issueStore';
import { format } from 'date-fns';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ParetoData {
  name: string;
  count: number;
  cumulative: number;
}

interface GroupedData {
  [key: string]: number;
}

interface RankData {
  rank: string;
  count: number;
}

interface StatusData {
  status: string;
  count: number;
}

interface RankGroupedData {
  rank: string;
  '25% completion': number;
  '50% completion': number;
  '75% completion': number;
  '100% completion': number;
  total: number;
}

interface StatusGroupedData {
  status: string;
  'RANK A': number;
  'RANK B': number;
  'RANK C': number;
  total: number;
}

interface Issue {
  id: number;
  type: string;
  rank: string;
  line_name: string;
  status: IssueStatus;
  completion_date: string;
  created_on: string;
}

type IssueStatus = 
  | 'waiting_response' 
  | 'inprogress' 
  | 'waiting_approval' 
  | 'completed';

export default function ProjectDataAnalysis() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentProject,
    isLoading: projectLoading,
    error: projectError,
    fetchProject,
    updateProject,
  } = useProjectStore();

  const {
    lines,
    isLoading: linesLoading,
    error: linesError,
    fetchLines,
  } = useLineStore();

  const {
    issues: storeIssues,
    loading: issuesLoading,
    error: issuesError,
    fetchIssues,
    deleteIssue,
  } = useIssueStore();

  // Add filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [paretoData, setParetoData] = useState<ParetoData[]>([]);
  const [rankData, setRankData] = useState<RankData[]>([]);
  const [statusData, setStatusData] = useState<StatusData[]>([]);
  const [rankGroupedData, setRankGroupedData] = useState<RankGroupedData[]>([]);
  const [statusGroupedData, setStatusGroupedData] = useState<StatusGroupedData[]>([]);
  const chartRef = useRef<HTMLDivElement>(null);

  // Function to export chart as PDF
  const exportToPDF = async () => {
    if (!chartRef.current || !currentProject) return;

    try {
      // Target A4 portrait dimensions rather than current screen size
      const pxPerMm = 96 / 25.4; // approximate CSS pixels per mm at 96 DPI
      const a4WidthPx = 210 * pxPerMm;
      const a4HeightPx = 297 * pxPerMm;

      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        width: a4WidthPx,
        height: a4HeightPx,
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 30;

      // Add title
      pdf.setFontSize(18);
      const title = `${currentProject.code} - ${currentProject.name} - ${groupBy === 'Rank' ? 'Summary BIRA by Status' : 'Summary BIRA by Rank'}`;
      pdf.text(title, pdfWidth / 2, 15, { align: 'center' });

      // Add filter information
      pdf.setFontSize(10);
      const filterText = `Group By: ${groupBy || 'None'}${startDate ? `, Start Date: ${startDate}` : ''}${endDate ? `, End Date: ${endDate}` : ''}`;
      pdf.text(filterText, pdfWidth / 2, 25, { align: 'center' });

      // Add chart image
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      // Add footer with date
      pdf.setFontSize(8);
      pdf.text(`Generated on ${format(new Date(), 'PPP')} at ${format(new Date(), 'p')}`, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
      
      // Format the current date and time for the filename
      const now = new Date();
      const dateTimeStr = format(now, 'yyyyMMdd_HHmmss');
      
      // Save the PDF with the new naming format
      pdf.save(`${currentProject.code || currentProject.name.replace(/\s+/g, '_')}_${groupBy.toLowerCase()}_analysis_${dateTimeStr}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export chart to PDF. Please try again.');
    }
  };

  const generateParetoData = () => {
    console.log('Store Issues:', storeIssues);
    if (!storeIssues || storeIssues.length === 0) {
      console.log('No issues found');
      return;
    }

    // Filter issues based on date range if provided
    let filteredIssues = storeIssues;
    if (startDate && endDate) {
      console.log('Date Range:', { startDate, endDate });
      filteredIssues = storeIssues.filter(issue => {
        const issueDate = new Date(issue.created_on);
        const start = new Date(startDate);
        const end = new Date(endDate);
        // Set end date to end of day
        end.setHours(23, 59, 59, 999);

        const isInRange = issueDate >= start && issueDate <= end;
        console.log('Issue date check:', {
          issueDate,
          start,
          end,
          isInRange
        });
        return isInRange;
      });
    } else {
      console.log('No date range selected, using all issues');
    }
    console.log('Filtered Issues:', filteredIssues);

    // Group issues based on selected groupBy field
    const groupedData: GroupedData = {};
    const groupByField = groupBy.toLowerCase().replace(/\s+/g, '_');
    console.log('GroupBy Field:', groupByField);

    // For the new structured data format
    if (groupByField === 'rank') {
      // Initialize data structure for rank grouping
      const rankData: { [key: string]: { [key: string]: number } } = {
        'RANK A': { '25% completion': 0, '50% completion': 0, '75% completion': 0, '100% completion': 0, total: 0 },
        'RANK B': { '25% completion': 0, '50% completion': 0, '75% completion': 0, '100% completion': 0, total: 0 },
        'RANK C': { '25% completion': 0, '50% completion': 0, '75% completion': 0, '100% completion': 0, total: 0 },
        'TOTAL': { '25% completion': 0, '50% completion': 0, '75% completion': 0, '100% completion': 0, total: 0 }
      };

      // Process each issue
      filteredIssues.forEach(issue => {
        const rank = issue.rank || 'Unknown';
        const status = issue.status;
        let completionCategory = '';

        // Map status to completion category using a switch statement
        switch(status) {
          case 'waiting_response':
            completionCategory = '25% completion';
            break;
          case 'inprogress':
            completionCategory = '50% completion';
            break;
          case 'waiting_approval':
            completionCategory = '75% completion';
            break;
          case 'completed':
            completionCategory = '100% completion';
            break;
          default:
            return; // Skip if status doesn't match any category
        }

        // Normalize rank to match our data structure keys
        let normalizedRank = '';
        if (rank.toUpperCase().includes('A')) {
          normalizedRank = 'RANK A';
        } else if (rank.toUpperCase().includes('B')) {
          normalizedRank = 'RANK B';
        } else if (rank.toUpperCase().includes('C')) {
          normalizedRank = 'RANK C';
        } else {
          return; // Skip if rank doesn't match our categories
        }

        // Increment counts for the specific rank and completion category
        rankData[normalizedRank][completionCategory]++;
        rankData[normalizedRank].total++;
        rankData['TOTAL'][completionCategory]++;
        rankData['TOTAL'].total++;
        
        console.log(`Added issue with rank ${normalizedRank} and status ${status} (${completionCategory})`);
      });

      // Convert to array format for the component state
      const formattedRankData = Object.entries(rankData).map(([rank, data]) => ({
        rank,
        '25% completion': data['25% completion'],
        '50% completion': data['50% completion'],
        '75% completion': data['75% completion'],
        '100% completion': data['100% completion'],
        total: data.total
      }));

      setRankGroupedData(formattedRankData);
      console.log('Rank Grouped Data:', formattedRankData);
    } else if (groupByField === 'status') {
      // Initialize data structure for status grouping
      const statusData: { [key: string]: { [key: string]: number } } = {
        '25% completion': { 'RANK A': 0, 'RANK B': 0, 'RANK C': 0, total: 0 },
        '50% completion': { 'RANK A': 0, 'RANK B': 0, 'RANK C': 0, total: 0 },
        '75% completion': { 'RANK A': 0, 'RANK B': 0, 'RANK C': 0, total: 0 },
        '100% completion': { 'RANK A': 0, 'RANK B': 0, 'RANK C': 0, total: 0 },
        'TOTAL': { 'RANK A': 0, 'RANK B': 0, 'RANK C': 0, total: 0 }
      };

      // Process each issue
      filteredIssues.forEach(issue => {
        const rank = issue.rank || 'Unknown';
        const status = issue.status;
        let completionCategory = '';

        // Map status to completion category using a switch statement
        switch(status) {
          case 'waiting_response':
            completionCategory = '25% completion';
            break;
          case 'inprogress':
            completionCategory = '50% completion';
            break;
          case 'waiting_approval':
            completionCategory = '75% completion';
            break;
          case 'completed':
            completionCategory = '100% completion';
            break;
          default:
            return; // Skip if status doesn't match any category
        }

        // Normalize rank to match our data structure keys
        let normalizedRank = '';
        if (rank.toUpperCase().includes('A')) {
          normalizedRank = 'RANK A';
        } else if (rank.toUpperCase().includes('B')) {
          normalizedRank = 'RANK B';
        } else if (rank.toUpperCase().includes('C')) {
          normalizedRank = 'RANK C';
        } else {
          return; // Skip if rank doesn't match our categories
        }

        // Increment counts for the specific status and rank
        statusData[completionCategory][normalizedRank]++;
        statusData[completionCategory].total++;
        statusData['TOTAL'][normalizedRank]++;
        statusData['TOTAL'].total++;
        
        console.log(`Added issue with rank ${normalizedRank} and status ${status} (${completionCategory})`);
      });

      // Convert to array format for the component state
      const formattedStatusData = Object.entries(statusData).map(([status, data]) => ({
        status,
        'RANK A': data['RANK A'],
        'RANK B': data['RANK B'],
        'RANK C': data['RANK C'],
        total: data.total
      }));

      setStatusGroupedData(formattedStatusData);
      console.log('Status Grouped Data:', formattedStatusData);
    }

    // Original Pareto chart data processing (keeping for backward compatibility)
    filteredIssues.forEach(issue => {
      let key = '';
      switch(groupByField) {
        case 'rank':
          key = String(issue.rank || 'Unknown');
          break;
        case 'status':
          key = String(issue.status || 'Unknown');
          break;
        default:
          key = 'Unknown';
      }
      console.log('Processing issue:', { type: issue.type, key });
      groupedData[key] = (groupedData[key] || 0) + 1;
    });
    console.log('Grouped Data:', groupedData);

    // Sort data in descending order
    const sortedData = Object.entries(groupedData)
      .sort(([, a], [, b]) => b - a)
      .map(([name, count]) => ({ name, count }));
    console.log('Sorted Data:', sortedData);

    // Calculate cumulative percentage
    const total = sortedData.reduce((sum, item) => sum + item.count, 0);
    let cumulative = 0;

    const paretoData = sortedData.map(item => {
      cumulative += (item.count / total) * 100;
      return {
        name: item.name,
        count: item.count,
        cumulative: Math.round(cumulative),
      };
    });

    console.log('Final Pareto Data:', paretoData);
    setParetoData(paretoData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with groupBy:', groupBy);
    if (!groupBy) {
      console.log('No groupBy selected');
      return;
    }
    generateParetoData();
  };

  const handleGroupByChange = (event: SelectChangeEvent<string>) => {
    setGroupBy(event.target.value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    const formattedDay = day < 10 ? `0${day}` : day;

    return `${formattedDay}-${month}-${year}`;
  };

  React.useEffect(() => {
    console.log('ParetoData state updated:', paretoData);
  }, [paretoData]);

  React.useEffect(() => {
    if (id) {
      fetchProject(parseInt(id, 10));
      fetchLines({ project_id: parseInt(id, 10) });
      fetchIssues({ project_id: parseInt(id, 10) });
    }
  }, [id, fetchProject, fetchLines, fetchIssues]);

  if (projectLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (projectError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{projectError}</Typography>
      </Box>
    );
  }

  if (!currentProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Project not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs 
          items={[
            { label: 'Events', path: '/projects' },
            { label: currentProject.name, path: `/projects/${id}` },
            { label: 'Data Analysis' }
          ]} 
        />
      </Box>
      <Grid container spacing={3}>
        {/* Main Content Area */}
        <Grid item xs={12} md={12}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={() => navigate(`/projects/${id}`)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
              {currentProject.name}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Project Details */}
            <Grid item xs={12} md={12}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Event Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography>
                      {currentProject.name}
                    </Typography>
                  </Grid>
                
                  <Grid item xs={4} sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      <Link component={RouterLink} to={`/projects/${currentProject.id}/lines`}
                        underline="hover" color="inherit">
                        Lines
                      </Link>
                    </Typography>
                    <Typography variant="h4">
                      {currentProject.lines_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Issues
                    </Typography>
                    <Typography variant="h4">
                      {currentProject.issues_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created On
                    </Typography>
                    <Typography>
                      {formatDate(currentProject.created_on)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Data Analysis */}
              <Paper sx={{ p: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">DATA ANALYSIS</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    {/* Left Column - Filter Form */}
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Filter Issues
                      </Typography>
                      <form onSubmit={handleSubmit}>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Box>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Group By</InputLabel>
                          <Select
                            value={groupBy}
                            label="Group By"
                            onChange={handleGroupByChange}
                          >
                            <MenuItem value="Rank">Rank</MenuItem>
                            <MenuItem value="Status">Status</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                        >
                          Apply Filters
                        </Button>
                      </form>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={8}>
                    {/* Right Column - Charts */}
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            Pareto Analysis
                            {paretoData.length > 0 && (
                              <Tooltip title="Export to PDF">
                                <IconButton 
                                  size="small" 
                                  color="primary" 
                                  onClick={exportToPDF}
                                  sx={{ ml: 1 }}
                                >
                                  <FileDownloadIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Typography>
                          {paretoData.length > 0 ? (
                            <div ref={chartRef}>
                              {groupBy === 'Rank' && (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                                        From
                                      </Typography>
                                      <TextField 
                                        type="date"
                                        size="small" 
                                        value={startDate} 
                                        disabled 
                                        sx={{ 
                                          mt: 1,
                                          mx: 1, 
                                          width: '180px',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '1px',
                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                            borderStyle: 'solid'
                                          }
                                        }}
                                      />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                      <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                                        To
                                      </Typography>
                                      <TextField 
                                        type="date"
                                        size="small" 
                                        value={endDate} 
                                        disabled 
                                        sx={{ 
                                          mt: 1,
                                          mx: 1, 
                                          width: '180px',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '1px',
                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                            borderStyle: 'solid'
                                          }
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  <Typography variant="h6" align="center" sx={{ mt: 2, mb: 1 }}>
                                    SUMMARY BIRA BY STATUS
                                  </Typography>
                                  <ResponsiveContainer width="100%" height={300}>
                                    <ComposedChart 
                                      data={rankGroupedData.filter(item => item.rank !== 'TOTAL')}
                                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                    >
                                      <CartesianGrid 
                                        strokeDasharray="3 3" 
                                        vertical={true}
                                        stroke="#ccc"
                                      />
                                      <XAxis dataKey="rank" axisLine={{ stroke: '#333', strokeWidth: 1 }} tickLine={{ stroke: '#333' }} />
                                      <YAxis 
                                        label={{ value: 'BIRA ISSUED', angle: -90, position: 'insideLeft' }} 
                                        axisLine={{ stroke: '#333', strokeWidth: 1 }} 
                                        tickLine={{ stroke: '#333' }}
                                        allowDecimals={false}
                                        domain={[0, 'dataMax']}
                                        tickCount={6}
                                        tickFormatter={(value) => Math.floor(value) === value ? value : ''}
                                      />
                                      <RechartsTooltip />
                                      <Bar dataKey="25% completion" fill="#cc3300" name="25% completion" />
                                      <Bar dataKey="50% completion" fill="#ff9900" name="50% completion" />
                                      <Bar dataKey="75% completion" fill="#ffc658" name="75% completion" />
                                      <Bar dataKey="100% completion" fill="#99cc00" name="100% completion" />
                                      <Bar dataKey="total" fill="grey" name="TOTAL" />
                                    </ComposedChart>
                                  </ResponsiveContainer>
                                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell></TableCell>
                                          <TableCell align="center">25% completion</TableCell>
                                          <TableCell align="center">50% completion</TableCell>
                                          <TableCell align="center">75% completion</TableCell>
                                          <TableCell align="center">100% completion</TableCell>
                                          <TableCell align="center">TOTAL</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {rankGroupedData.map((row) => (
                                          <TableRow
                                            key={row.rank}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, 
                                                 backgroundColor: row.rank === 'TOTAL' ? '#f5f5f5' : 'inherit' }}
                                          >
                                            <TableCell component="th" scope="row">
                                              {row.rank === 'RANK A' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: 'red', mr: 1 }}></Box>
                                                  {row.rank}
                                                </Box>
                                              )}
                                              {row.rank === 'RANK B' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: 'yellow', mr: 1 }}></Box>
                                                  {row.rank}
                                                </Box>
                                              )}
                                              {row.rank === 'RANK C' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: 'green', mr: 1 }}></Box>
                                                  {row.rank}
                                                </Box>
                                              )}
                                              {row.rank === 'TOTAL' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: 'grey', mr: 1 }}></Box>
                                                  {row.rank}
                                                </Box>
                                              )}
                                            </TableCell>
                                            <TableCell align="center">{row['25% completion']}</TableCell>
                                            <TableCell align="center">{row['50% completion']}</TableCell>
                                            <TableCell align="center">{row['75% completion']}</TableCell>
                                            <TableCell align="center">{row['100% completion']}</TableCell>
                                            <TableCell align="center">{row.total}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}
                              
                              {groupBy === 'Status' && (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                                        From
                                      </Typography>
                                      <TextField 
                                        type="date"
                                        size="small" 
                                        value={startDate} 
                                        disabled 
                                        sx={{ 
                                          mt: 1,
                                          mx: 1, 
                                          width: '180px',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '1px',
                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                            borderStyle: 'solid'
                                          }
                                        }}
                                      />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                      <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                                        To
                                      </Typography>
                                      <TextField 
                                        type="date"
                                        size="small" 
                                        value={endDate} 
                                        disabled 
                                        sx={{ 
                                          mt: 1,
                                          mx: 1, 
                                          width: '180px',
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '1px',
                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                            borderStyle: 'solid'
                                          }
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  <Typography variant="h6" align="center" sx={{ mt: 2, mb: 1 }}>
                                    SUMMARY BIRA BY RANK
                                  </Typography>
                                  <ResponsiveContainer width="100%" height={300}>
                                    <ComposedChart 
                                      data={statusGroupedData.filter(item => item.status !== 'TOTAL')}
                                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                    >
                                      <CartesianGrid 
                                        strokeDasharray="3 3" 
                                        vertical={true}
                                        stroke="#ccc"
                                      />
                                      <XAxis dataKey="status" axisLine={{ stroke: '#333', strokeWidth: 1 }} tickLine={{ stroke: '#333' }} />
                                      <YAxis 
                                        label={{ value: 'BIRA ISSUED', angle: -90, position: 'insideLeft' }} 
                                        axisLine={{ stroke: '#333', strokeWidth: 1 }} 
                                        tickLine={{ stroke: '#333' }}
                                        allowDecimals={false}
                                        domain={[0, 'dataMax']}
                                        tickCount={6}
                                        tickFormatter={(value) => Math.floor(value) === value ? value : ''}
                                      />
                                      <RechartsTooltip />
                                      <Bar dataKey="RANK A" fill="red" name="RANK A" />
                                      <Bar dataKey="RANK B" fill="yellow" name="RANK B" />
                                      <Bar dataKey="RANK C" fill="green" name="RANK C" />
                                      <Bar dataKey="total" fill="grey" name="TOTAL" />
                                    </ComposedChart>
                                  </ResponsiveContainer>
                                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell></TableCell>
                                          <TableCell align="center">RANK A</TableCell>
                                          <TableCell align="center">RANK B</TableCell>
                                          <TableCell align="center">RANK C</TableCell>
                                          <TableCell align="center">TOTAL</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {statusGroupedData.map((row) => (
                                          <TableRow
                                            key={row.status}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, 
                                                 backgroundColor: row.status === 'TOTAL' ? '#f5f5f5' : 'inherit' }}
                                          >
                                            <TableCell component="th" scope="row">
                                              {row.status === '25% completion' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: '#cc3300', mr: 1 }}></Box>
                                                  {row.status}
                                                </Box>
                                              )}
                                              {row.status === '50% completion' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: '#ff9900', mr: 1 }}></Box>
                                                  {row.status}
                                                </Box>
                                              )}
                                              {row.status === '75% completion' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: '#cc9900', mr: 1 }}></Box>
                                                  {row.status}
                                                </Box>
                                              )}
                                              {row.status === '100% completion' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: '#99cc00', mr: 1 }}></Box>
                                                  {row.status}
                                                </Box>
                                              )}
                                              {row.status === 'TOTAL' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <Box sx={{ width: 16, height: 16, backgroundColor: '#808080', mr: 1 }}></Box>
                                                  {row.status}
                                                </Box>
                                              )}
                                            </TableCell>
                                            <TableCell align="center">{row['RANK A']}</TableCell>
                                            <TableCell align="center">{row['RANK B']}</TableCell>
                                            <TableCell align="center">{row['RANK C']}</TableCell>
                                            <TableCell align="center">{row.total}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}
                              
                              {/* Original Pareto Chart (keeping for backward compatibility) */}
                              {false && (
                                <ResponsiveContainer width="100%" height={400}>
                                  <ComposedChart 
                                    data={paretoData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                      dataKey="name" 
                                      angle={-45}
                                      textAnchor="end"
                                      height={70}
                                      interval={0}
                                    />
                                    <YAxis 
                                      yAxisId="left" 
                                      label={{ 
                                        value: 'Frequency', 
                                        angle: -90, 
                                        position: 'insideLeft',
                                        offset: -5
                                      }} 
                                    />
                                    <YAxis 
                                      yAxisId="right" 
                                      orientation="right" 
                                      unit="%" 
                                      label={{ 
                                        value: 'Cumulative %', 
                                        angle: 90, 
                                        position: 'insideRight',
                                        offset: 5
                                      }} 
                                    />
                                    <RechartsTooltip 
                                      formatter={(value: any, name: string) => [
                                        value + (name === 'Cumulative %' ? '%' : ''),
                                        name
                                      ]}
                                    />
                                    <Legend verticalAlign="top" height={36}/>
                                    <Bar
                                      yAxisId="left"
                                      dataKey="count"
                                      fill="#cc3300"
                                      name="Frequency"
                                      barSize={40}
                                    >
                                      {paretoData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={`hsl(${index * (360 / paretoData.length)}, 70%, 60%)`} />
                                      ))}
                                    </Bar>
                                    <Line
                                      yAxisId="right"
                                      type="monotone"
                                      dataKey="cumulative"
                                      stroke="#ff7300"
                                      name="Cumulative %"
                                      strokeWidth={2}
                                      dot={{ r: 4 }}
                                      activeDot={{ r: 6 }}
                                    />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              )}
                            </div>
                          ) : (
                            <Box
                              sx={{
                                height: 400,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography color="text.secondary">
                                {issuesLoading
                                  ? 'Loading...'
                                  : 'Select filters and click Apply to generate the Pareto chart'}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};